import React, { useState, useCallback } from "react";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, Palette, Save } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import PaletteCard from "./branding/PaletteCard";
import ImagesCard from "./branding/ImagesCard";
import CertificateCard from "./branding/CertificateCard";
import BrandingPreview from "./branding/BrandingPreview";
import SocialLinksCard from "./branding/SocialLinksCard";
// Relative on purpose: no "@" alias on the Jest side (cf. LanguageAwareField.jsx).
import useAdminLoad from "../hooks/useAdminLoad";
import LoadFailure from "../components/ui/LoadFailure";

/**
 * BrandingAdmin — orchestration only.
 * Avant B18 : 232L. Après : 90L + 3 sous-composants.
 */
export default function BrandingAdmin() {
  const t = useT("admin");
  const tv = useT("common");
  const [branding, setBranding] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/tenant/self/info");
    setTenant(data);
    const SOLUNARY_DEFAULTS = {
      primary_color: "#0B1020",
      accent_color: "#E6B85C",
      secondary_color: "#7FB8A4",
    };
    setBranding({ ...SOLUNARY_DEFAULTS, ...(data.branding || {}) });
  }, []);

  // BUG-051's class, on this screen too: a failed read left `branding` at `null` and the
  // early return below rendered a spinner that never stops — no message, no retry. Worse
  // than on a list screen, because a palette that never arrives would be SAVED from the
  // hardcoded defaults if the form ever rendered, replacing the practice's real colours.
  const { loadError, reload } = useAdminLoad(load);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tenant/branding", branding);
      toast.success(t("branding.saved"));
      load();
    } catch (e) {
      toast.error(extractApiError(e, t("avail.error")));
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url || data.file_url || (data.path ? `/api/uploads/${data.path}` : "");
      if (url) {
        setBranding({ ...branding, [field]: url });
        toast.success(t("branding.imageUploaded"));
      } else {
        toast.error(t("branding.urlError"));
      }
    } catch {
      toast.error(t("branding.uploadError"));
    } finally {
      setUploading(false);
    }
  };

  if (loadError) {
    return (
      <div data-testid="branding-admin" className="max-w-5xl">
        <LoadFailure
          message={extractApiError(loadError, tv("errors.loadFailed"), tv)}
          onRetry={reload}
          retryLabel={tv("actions.retry")}
          testId="admin-branding-load-error"
          retryTestId="admin-branding-retry"
        />
      </div>
    );
  }
  if (!branding) return <Loader2 className="w-6 h-6 animate-spin text-eh-muted" />;

  return (
    <div data-testid="branding-admin" className="space-y-8 max-w-5xl">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
            <Palette className="w-6 h-6 text-sol-gold" /> Branding
          </h1>
          <p className="text-eh-muted text-sm mt-1">{t("branding.subtitle")}</p>
        </div>
        <Button onClick={save} disabled={saving || uploading} data-testid="branding-save" className="bg-eh-ink text-eh-bg">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {t("actions.save")}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <PaletteCard branding={branding} setBranding={setBranding} />
          <ImagesCard branding={branding} setBranding={setBranding} onUpload={uploadFile} />
          <CertificateCard branding={branding} setBranding={setBranding} onUpload={uploadFile} tenant={tenant} />
          {/* THE HEADLINE AND THE SPECIALTY CHIPS MOVED to the public profile
              (`PublicProfileAdmin`), with the description, photo, country and languages the
              marketplace shows beside them: one screen, one save. This pointer stays so a
              practitioner who looks for them here is sent where they now live. Relative to
              THIS route, so it resolves under `/admin/*` and `/t/{slug}/admin/*` alike. */}
          <div className="bg-eh-cream/40 rounded-2xl border border-eh-border-soft p-5 flex flex-wrap items-center justify-between gap-3" data-testid="branding-profile-moved">
            <p className="text-sm text-eh-ink">{t("branding.profileMoved")}</p>
            <Link to="../profile" relative="path" className="text-sm font-medium underline underline-offset-4" data-testid="branding-profile-link">
              {t("branding.profileMovedLink")}
            </Link>
          </div>
          <SocialLinksCard branding={branding} setBranding={setBranding} />
        </div>
        <div className="lg:col-span-1">
          <BrandingPreview branding={branding} tenant={tenant} />
        </div>
      </div>
    </div>
  );
}
